/**
 * RMA create / edit form.
 *
 * Renders ONLY the form body (no AppNav / TitleBar). The parent ReturnView
 * owns page chrome. On successful save, navigates to /rma/:id/view.
 *
 * Create mode: no rma prop (POST /rmas/). Ticket→RMA handoff seeds values
 * from location.state.fromTicket (set by NewTicketView).
 * Edit mode: rma prop is the fetched RMA Immutable Map (PATCH /rmas/:id/).
 *
 * Contains:
 * - CreateReturnForm — default export, the form component
 * - buildPayload     — assembles the POST/PATCH body from Formik values + side state
 * - seedFromRma      — extracts contact state from an existing RMA Map
 */

import { Alert, AlertTitle, Box, Button, Checkbox, FormControlLabel, Grid, Stack, Typography } from '@mui/material';
import { blue } from '@mui/material/colors';
import { Form, Formik } from 'formik';
import { Map } from 'immutable';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { findAutoFillContact } from '@sophocles/components/contacts/hooks/useAutoFillContact';
import ImagePicker from '@sophocles/components/ImagePicker';
import IssueCategorySelectComponent from '@sophocles/components/IssueCategorySelectComponent';
import RadioValidationBox from '@sophocles/components/RadioValidationBox';
import {
  useCreateRmaMutation,
  useDeleteRmaImageMutation,
  usePatchRmaMutation,
} from '@sophocles/features/entities/rmas';
import { addToast } from '@sophocles/features/toast';
import { parseApiError } from '@sophocles/utils/apiErrors';
import ConflictingRMAChecker from '@sophocles/views/returns/components/ConflictingRMAChecker';
import SpecialInstructionsCollapse from '@sophocles/views/returns/components/CustomerSpecialInstructionsCollapse';
import SiteGroupInstructionsCollapse from '@sophocles/views/returns/components/SiteGroupInstructionsCollapse';
import { SettingsTextField } from '@sophocles/views/settings/components';

import { DetailRow, SectionCard } from '../components/ReturnSection';
import { billContactFields, isValidContactId, shipContactFields } from '../utils/contactPayload';
import { returnEntityLabels } from '../utils/tenantLabels';

import CreateReturnFormContacts from './CreateReturnFormContacts';
import ShippingModeSelect from './fields/ShippingModeSelect';

// ─── constants ────────────────────────────────────────────────────────────────

const DEFAULT_ISSUE_CATEGORY = 4;
const DEFAULT_ISSUE_SUBCATEGORY = 2;
// The radio-return-mismatch error is the one API validation failure this form
// singles out (to reset the radio picker); everything else surfaces generically.
const RADIO_ERROR_FIELDS = ['radio_returned_by_user_id'];

// ─── helpers ──────────────────────────────────────────────────────────────────

/** Build a plain-JS contact from inline RMA fields keyed by prefix (ship/bill). */
function buildInlineContact(rma, prefix) {
  const name = rma.get(`${prefix}_contact`, '');
  const email = rma.get(`${prefix}_email`, '');
  const phone = rma.get(`${prefix}_phone`, '');
  const address = rma.get(`${prefix}_address`, '');
  const address_2 = rma.get(`${prefix}_address_2`, '');
  const city = rma.get(`${prefix}_city`, '');
  const state = rma.get(`${prefix}_state`, '');
  const zip = rma.get(`${prefix}_zip`, '');
  const country = rma.get(`${prefix}_country`, '');
  const business_name = rma.get(`${prefix}_company_name`, '');

  if (!name && !email && !address) return null;

  return {
    id: null,
    name,
    business_name,
    email,
    phone1: phone,
    address,
    address_2,
    city,
    state,
    zipcode: zip,
    country,
    full_address_display: [address, address_2, [city, state, zip].filter(Boolean).join(', '), country]
      .filter(Boolean)
      .join(', '),
  };
}

/** Ensure a contact object has full_address_display populated. */
function ensureFullAddress(contact) {
  if (!contact || contact.full_address_display) return contact;
  return {
    ...contact,
    full_address_display: [
      contact.address,
      contact.address_2,
      [contact.city, contact.state, contact.zipcode].filter(Boolean).join(', '),
      contact.country,
    ]
      .filter(Boolean)
      .join(', '),
  };
}

/**
 * Extract contact state from an existing RMA Immutable Map.
 * Returns the three resolved contact objects for useState initialisation.
 */
function seedFromRma(rma) {
  const shippingFK = rma.get('shipping_contact', null);
  let selectedShippingContact = null;
  if (shippingFK && shippingFK.get && shippingFK.get('id')) {
    selectedShippingContact = ensureFullAddress(shippingFK.toJS ? shippingFK.toJS() : shippingFK);
  } else {
    selectedShippingContact = buildInlineContact(rma, 'ship');
  }

  const billingFK = rma.get('billing_contact', null);
  let selectedBillingContact = null;
  if (billingFK && billingFK.get && billingFK.get('id')) {
    selectedBillingContact = ensureFullAddress(billingFK.toJS ? billingFK.toJS() : billingFK);
  } else {
    selectedBillingContact = buildInlineContact(rma, 'bill');
  }

  const supportFK = rma.get('support_contact', null);
  let selectedReportingContact = null;
  if (supportFK && supportFK.get && supportFK.get('id')) {
    selectedReportingContact = ensureFullAddress(supportFK.toJS ? supportFK.toJS() : supportFK);
  } else {
    const supportName = rma.get('support_contact_name', '');
    const supportEmail = rma.get('support_contact_email', '');
    if (supportName || supportEmail) {
      selectedReportingContact = {
        id: null,
        name: supportName,
        business_name: '',
        email: supportEmail,
        phone1: rma.get('support_contact_phone', ''),
        address: '',
        city: '',
        state: '',
        zipcode: '',
        country: '',
        full_address_display: '',
      };
    }
  }

  return { selectedShippingContact, selectedBillingContact, selectedReportingContact };
}

/**
 * Assemble the POST/PATCH body. Scalar text fields come from Formik `values`;
 * external-component values come from side-state arguments.
 * Produces the exact same field set as the legacy IssueRmaPage.
 */
function buildPayload(
  values,
  {
    customer,
    siteGroup,
    site,
    ticket,
    issueSubCategory,
    oldRadio,
    noRadio,
    selectedShippingContact,
    selectedBillingContact,
    selectedReportingContact,
  },
) {
  const customerId = customer ? (customer.get ? customer.get('id', 0) : customer.id) : 0;
  const siteGroupId = siteGroup ? (siteGroup.get ? siteGroup.get('id', 0) : siteGroup.id) : 0;

  const payload = {
    customer_id: customerId,
    site_group_id: siteGroupId,
    site_id: site ? (site.get ? site.get('id', null) : site.id) : null,
    support_ticket_id: ticket ? (ticket.get ? ticket.get('id', null) : ticket.id) : null,
    issue_id: issueSubCategory,
    is_quick_ship: values.isQuickShip,
    unauthorized_swap: values.unauthorizedSwap,
    inbound_shipping_mode_id: values.inboundShippingMode,
    outbound_shipping_mode_id: values.outboundShippingMode,
    radio_returned_by_user_id: noRadio ? null : oldRadio ? (oldRadio.get ? oldRadio.get('id', 0) : oldRadio.id) : null,
    comments: values.comments,
    hidden_comments: values.hiddenComments,
    phone: null,
  };

  const siteGroupName = siteGroup && siteGroup.get ? siteGroup.get('name', '') : '';
  const customerName = customer && customer.get ? customer.get('name', '') : '';

  if (selectedShippingContact) {
    Object.assign(payload, shipContactFields(selectedShippingContact, { siteGroupName, customerName }));
    payload.shipping_contact_id =
      selectedShippingContact.id && isValidContactId(selectedShippingContact.id) && !selectedShippingContact._isAddress
        ? selectedShippingContact.id
        : null;
  }

  if (selectedBillingContact) {
    Object.assign(payload, billContactFields(selectedBillingContact, { siteGroupName, customerName }));
    payload.billing_contact_id =
      selectedBillingContact.id && isValidContactId(selectedBillingContact.id) && !selectedBillingContact._isAddress
        ? selectedBillingContact.id
        : null;
  }

  if (selectedReportingContact) {
    payload.support_contact_name = selectedReportingContact.name || selectedReportingContact.business_name || '';
    payload.support_contact_email = selectedReportingContact.email || '';
    payload.support_contact_phone = selectedReportingContact.phone1 || '';
    payload.support_contact_id =
      selectedReportingContact.id &&
      isValidContactId(selectedReportingContact.id) &&
      !selectedReportingContact._isAddress
        ? selectedReportingContact.id
        : null;
  }

  return payload;
}

// ─── component ────────────────────────────────────────────────────────────────

/**
 * @param {object} props
 * @param {import('immutable').Map|undefined} props.rma - present = edit mode
 */
export default function CreateReturnForm({ rma }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [createRma] = useCreateRmaMutation();
  const [patchRma] = usePatchRmaMutation();
  const [deleteRmaImage] = useDeleteRmaImageMutation();

  const isEditMode = Boolean(rma && !rma.isEmpty());
  const fromTicket = isEditMode ? null : (location.state?.fromTicket ?? null);

  // ── Formik initialValues — scalar fields only ──────────────────────────
  const initialValues = {
    comments: isEditMode ? (rma.get('comments', '') ?? '') : (fromTicket?.problemDescription ?? ''),
    hiddenComments: isEditMode ? (rma.get('hidden_comments', '') ?? '') : '',
    isQuickShip: isEditMode ? rma.get('is_quick_ship', false) : false,
    unauthorizedSwap: isEditMode ? rma.get('unauthorized_swap', false) : false,
    inboundShippingMode: isEditMode ? rma.getIn(['inbound_shipping_mode', 'id'], null) : null,
    outboundShippingMode: isEditMode ? rma.getIn(['outbound_shipping_mode', 'id'], null) : null,
  };

  // ── External-component state (not in Formik values) ─────────────────────
  // Follows EditTicketForm pattern: these are controlled by sub-components
  // that don't bind to Formik Field, so they live in useState.

  // The RMA-list page hands its picked customer/group context through
  // navigation state (plain {id, name} objects — every read below handles
  // plain and Immutable alike); a ticket handoff wins when both exist.
  const rmaContext = isEditMode ? null : (location.state?.rmaContext ?? null);
  const [siteGroup] = useState(
    isEditMode ? (rma.get('site_group', null) ?? Map()) : (fromTicket?.siteGroup ?? rmaContext?.siteGroup ?? Map()),
  );
  const [customer] = useState(
    isEditMode
      ? (rma.getIn(['site_group', 'customer'], null) ?? Map())
      : (fromTicket?.customer ?? rmaContext?.customer ?? Map()),
  );
  const [site] = useState(isEditMode ? rma.get('site', null) : (fromTicket?.site ?? null));
  const [ticket] = useState(isEditMode ? rma.get('ticket', null) : (fromTicket?.ticket ?? null));

  const entityLabels = returnEntityLabels(t, customer);

  const [issueCategory, setIssueCategory] = useState(
    isEditMode
      ? rma.getIn(['issue', 'category', 'id'], DEFAULT_ISSUE_CATEGORY)
      : (fromTicket?.issueCategoryId ?? DEFAULT_ISSUE_CATEGORY),
  );
  const [issueSubCategory, setIssueSubCategory] = useState(
    isEditMode
      ? rma.getIn(['issue', 'id'], DEFAULT_ISSUE_SUBCATEGORY)
      : (fromTicket?.issueId ?? DEFAULT_ISSUE_SUBCATEGORY),
  );

  // New RMAs seed the returned device from the originating site — the
  // relay-controller radio, else the CIC's, else the Z-Wave controller's —
  // so an RMA raised from a structure never asks for an ICCID the flow
  // already knows. The user can still clear or swap it in the lookup box.
  // Shallow Map wrap: RadioValidationBox reads radio.get('iccid'), the same
  // shape its own lookup path hands back via onRadioSelect(Map(radio)).
  const [oldRadio, setOldRadio] = useState(() => {
    if (isEditMode) return rma.get('radio_returned_by_user', null);
    const fromSite = fromTicket?.site;
    const seededRadio =
      fromSite?.relay_controller?.radio ?? fromSite?.cic?.radio ?? fromSite?.z_wave_controller?.radio ?? null;
    return seededRadio ? Map(seededRadio) : null;
  });
  const [noRadio, setNoRadio] = useState(false);
  const [noActiveRMA, setNoActiveRMA] = useState(true);

  const [picture, setPicture] = useState(isEditMode ? rma.get('picture', null) : null);
  const [pictureFile, setPictureFile] = useState(null);

  const [selectedReportingContact, setSelectedReportingContact] = useState(null);
  const [selectedShippingContact, setSelectedShippingContact] = useState(null);
  const [selectedBillingContact, setSelectedBillingContact] = useState(null);
  const [shippingValidationValid, setShippingValidationValid] = useState(true);
  const [billingValidationValid, setBillingValidationValid] = useState(true);
  const [reportingValidationValid, setReportingValidationValid] = useState(true);

  const [customerInstructionsFollowed, setCustomerInstructionsFollowed] = useState(true);
  const [siteGroupInstructionsFollowed, setSiteGroupInstructionsFollowed] = useState(true);

  const [loading, setLoading] = useState(false);

  // ── Seed contacts from RMA on mount (edit mode) ──────────────────────────
  useEffect(() => {
    if (!isEditMode) return;
    const seeded = seedFromRma(rma);
    setSelectedShippingContact(seeded.selectedShippingContact);
    setSelectedBillingContact(seeded.selectedBillingContact);
    setSelectedReportingContact(seeded.selectedReportingContact);
  }, []); // runs once on mount

  // ── Auto-fill contacts for new RMAs once customer/siteGroup resolve ──────
  const autoFilledRef = useRef(false);
  useEffect(() => {
    if (isEditMode || autoFilledRef.current) return;
    const customerId = customer ? (customer.get ? customer.get('id', 0) : customer.id) : 0;
    const siteGroupId = siteGroup ? (siteGroup.get ? siteGroup.get('id', 0) : siteGroup.id) : 0;
    if (!customerId || !siteGroupId) return;

    autoFilledRef.current = true;
    const customerName = customer.get ? customer.get('name', 'Customer') : customer.name || 'Customer';
    const siteGroupName = siteGroup.get ? siteGroup.get('name', 'Site Group') : siteGroup.name || 'Site Group';

    const doAutoFill = async () => {
      const shippingContact = await findAutoFillContact({
        customerId,
        siteGroupId,
        type: 'shipping',
        context: 'rma',
        customerName,
        siteGroupName,
      });
      if (shippingContact) setSelectedShippingContact(shippingContact);

      const billingContact = await findAutoFillContact({
        customerId,
        siteGroupId,
        type: 'billing',
        context: 'rma',
        customerName,
        siteGroupName,
      });
      if (billingContact) setSelectedBillingContact(billingContact);

      if (ticket) {
        const ticketContact = ticket.get ? ticket.get('contact') : ticket.contact;
        if (ticketContact) {
          const name = ticketContact.get
            ? ticketContact.get('name', '') || ticketContact.get('business_name', '')
            : ticketContact.name || ticketContact.business_name || '';
          const email = ticketContact.get ? ticketContact.get('email', '') : ticketContact.email || '';
          const phone = ticketContact.get
            ? ticketContact.get('phone', '') || ticketContact.get('phone1', '')
            : ticketContact.phone || ticketContact.phone1 || '';
          if (name || email) {
            const raw = ticketContact.toJS ? ticketContact.toJS() : { ...ticketContact };
            setSelectedReportingContact({ ...raw, name, email, phone1: phone });
          }
        }
      }
    };

    doAutoFill().catch(err => console.error('CreateReturnForm: auto-fill error', err));
  }, [customer, siteGroup, ticket, isEditMode]);

  // ── Handlers ─────────────────────────────────────────────────────────────

  const handleIssueCategoryChange = useCallback((category, subCategory) => {
    setIssueCategory(category);
    setIssueSubCategory(subCategory);
  }, []);

  // When the user clears a staged file that replaces an already-saved photo,
  // delete the saved photo from the server immediately so the cleared state
  // matches what the server holds.
  const handlePictureSelect = useCallback(
    selectedFile => {
      if (selectedFile === null && picture) {
        const rmaId = rma ? rma.get('id', 0) : 0;
        if (rmaId) {
          deleteRmaImage({ id: rmaId })
            .unwrap()
            .then(() => {
              setPicture(null);
              dispatch(
                addToast({
                  title: t('general.success', 'Success'),
                  message: t('rma.rmaImageDeleted', 'RMA image deleted'),
                  level: 'success',
                  dismissible: true,
                }),
              );
            })
            .catch(() => {});
        }
      }
      setPictureFile(selectedFile);
    },
    [deleteRmaImage, dispatch, picture, rma],
  );

  // ── Formik onSubmit ───────────────────────────────────────────────────────

  const handleSubmit = useCallback(
    (values, { setSubmitting }) => {
      if (!shippingValidationValid || !billingValidationValid) {
        dispatch(
          addToast({
            message: t(
              'rma.addressFieldsIncomplete',
              'Please complete all required address fields for shipping and billing before submitting.',
            ),
            level: 'error',
          }),
        );
        setSubmitting(false);
        return;
      }
      if (!reportingValidationValid) {
        dispatch(
          addToast({
            message: t(
              'rma.reportingFieldsIncomplete',
              'Please complete reporting contact fields (name, email OR phone) before submitting.',
            ),
            level: 'error',
          }),
        );
        setSubmitting(false);
        return;
      }

      const payload = buildPayload(values, {
        customer,
        siteGroup,
        site,
        ticket,
        issueSubCategory,
        oldRadio,
        noRadio,
        selectedShippingContact,
        selectedBillingContact,
        selectedReportingContact,
      });

      const rmaId = rma ? rma.get('id', 0) : 0;
      setLoading(true);

      const handleRadioError = (err, radio) => {
        const { fieldErrors, nonFieldMessages } = parseApiError(err, {
          knownFields: RADIO_ERROR_FIELDS,
          genericMessage: t('rma.saveError', 'Error saving RMA.'),
        });
        if (fieldErrors.radio_returned_by_user_id) {
          dispatch(addToast({ message: fieldErrors.radio_returned_by_user_id.join(' '), level: 'error' }));
          setOldRadio(null);
          setTimeout(() => setOldRadio(radio), 0);
          return;
        }
        dispatch(addToast({ message: nonFieldMessages[0] ?? t('rma.saveError', 'Error saving RMA.'), level: 'error' }));
      };

      // Picture upload errors must NOT block navigation (legacy behavior): the
      // RMA write already succeeded, so swallow the picture failure into a toast.
      const uploadPictureIfStaged = targetId => {
        if (!pictureFile) return Promise.resolve();
        return patchRma({ id: targetId, picture: pictureFile })
          .unwrap()
          .then(() =>
            dispatch(
              addToast({
                title: t('general.success', 'Success'),
                message: t('rma.rmaPictureUpdated', 'RMA picture updated'),
                level: 'success',
                dismissible: true,
              }),
            ),
          )
          .catch(() =>
            dispatch(
              addToast({
                title: t('general.error', 'Error'),
                message: t('rma.errorSavingPicture', 'Error saving picture'),
                level: 'error',
                dismissible: true,
              }),
            ),
          );
      };

      if (rmaId !== 0) {
        patchRma({ id: rmaId, body: payload })
          .unwrap()
          .then(() => {
            dispatch(
              addToast({
                title: t('general.success', 'Success'),
                message: t('rma.rmaUpdated', 'RMA updated'),
                level: 'success',
                dismissible: true,
              }),
            );
            uploadPictureIfStaged(rmaId).then(() => {
              setLoading(false);
              setSubmitting(false);
              navigate(`/rma/${rmaId}/view`, { replace: true });
            });
          })
          .catch(err => {
            setLoading(false);
            setSubmitting(false);
            handleRadioError(err, oldRadio);
          });
      } else {
        createRma({ body: payload })
          .unwrap()
          .then(res => {
            setLoading(false);
            setSubmitting(false);
            dispatch(
              addToast({
                title: t('general.success', 'Success'),
                message: t('rma.rmaCreated', 'RMA created!'),
                level: 'success',
                dismissible: true,
              }),
            );
            const newId = res?.id ?? 0;
            navigate(`/rma/${newId}/view`, { replace: true });
          })
          .catch(err => {
            setLoading(false);
            setSubmitting(false);
            handleRadioError(err, oldRadio);
          });
      }
    },
    [
      shippingValidationValid,
      billingValidationValid,
      reportingValidationValid,
      customer,
      siteGroup,
      site,
      ticket,
      issueSubCategory,
      oldRadio,
      noRadio,
      selectedShippingContact,
      selectedBillingContact,
      selectedReportingContact,
      pictureFile,
      rma,
      createRma,
      patchRma,
      dispatch,
      navigate,
    ],
  );

  // ── Derived display values ────────────────────────────────────────────────

  const customerId = customer ? (customer.get ? customer.get('id', 0) : (customer.id ?? 0)) : 0;
  const siteGroupId = siteGroup ? (siteGroup.get ? siteGroup.get('id', 0) : (siteGroup.id ?? 0)) : 0;
  const legacyOldIccid = rma ? rma.get('legacy_incoming_iccid', null) : null;
  const legacyNewIccid = rma ? rma.get('legacy_new_iccid', null) : null;
  const legacyReplacedIccid = rma ? rma.get('legacy_replaced_iccid', null) : null;

  const ticketNumber = ticket ? (ticket.get ? ticket.get('ticket_number', '') : ticket.ticket_number || '') : '';
  const ticketProblem = ticket
    ? ticket.get
      ? ticket.get('problem_description', '')
      : ticket.problem_description || ''
    : '';
  const ticketId = ticket ? (ticket.get ? ticket.get('id') : ticket.id) : null;

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ display: 'flex', overflow: 'auto', flex: 1 }}>
      <Box sx={{ width: '100%' }}>
        {ticket && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <AlertTitle>
              {t('rma.creatingRmaFromTicket', 'Creating RMA from Support Ticket #{{num}}', { num: ticketNumber })}
            </AlertTitle>
            {ticketProblem && (
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>{t('rma.issueColon', 'Issue:')}</strong>{' '}
                {ticketProblem.length > 150 ? `${ticketProblem.substring(0, 150)}…` : ticketProblem}
              </Typography>
            )}
            <Typography variant="caption" display="block">
              {t('rma.contactPrefilledInfo', 'Contact information pre-filled from ticket and primary RMA contact')}
            </Typography>
          </Alert>
        )}

        <Formik initialValues={initialValues} enableReinitialize onSubmit={handleSubmit}>
          {({ values, setFieldValue, submitForm, isSubmitting }) => {
            const canSubmit =
              (oldRadio != null || noRadio) &&
              customerInstructionsFollowed &&
              siteGroupInstructionsFollowed &&
              noActiveRMA &&
              !isSubmitting &&
              !loading;

            return (
              <Form>
                {/* ── Context ── */}
                {(customerId !== 0 || siteGroupId !== 0 || ticket) && (
                  <SectionCard title={t('rma.context', 'Context')}>
                    {customerId !== 0 && (
                      <DetailRow label={entityLabels.customer}>
                        {customerId}.{' '}
                        {customer.get
                          ? customer.get('name', t('rma.noneLower', 'none'))
                          : customer.name || t('rma.noneLower', 'none')}{' '}
                        <Link style={{ fontSize: 13 }} target="_blank" to={`/c/${customerId}/plants`}>
                          {t('rma.link', '(link)')}
                        </Link>
                      </DetailRow>
                    )}
                    {siteGroupId !== 0 && (
                      <DetailRow label={entityLabels.siteGroup}>
                        {`${siteGroup.get ? siteGroup.get('name', '') : siteGroup.name || ''} — ${siteGroup.get ? siteGroup.get('description', '') : siteGroup.description || ''}`}{' '}
                        <Link
                          style={{ fontSize: 13 }}
                          target="_blank"
                          to={`/c/${customerId}/plant/${siteGroupId}/structures`}
                        >
                          {t('rma.link', '(link)')}
                        </Link>
                      </DetailRow>
                    )}
                    {site && (
                      <DetailRow label={entityLabels.structure}>
                        {`${site.get ? site.get('name', '') : site.name || ''} — ${site.get ? site.get('description', '') : site.description || ''}`}{' '}
                        <Link
                          style={{ fontSize: 13 }}
                          target="_blank"
                          to={`/c/${customerId}/plant/${siteGroupId}/structure/${site.get ? site.get('id') : site.id}`}
                        >
                          {t('rma.link', '(link)')}
                        </Link>
                      </DetailRow>
                    )}
                    {ticket && (
                      <DetailRow label={t('rma.supportTicket', 'Support Ticket')}>
                        #{ticketNumber}{' '}
                        <Link style={{ fontSize: 13 }} target="_blank" to={`/support/ticket/${ticketId}/view`}>
                          {t('rma.link', '(link)')}
                        </Link>
                      </DetailRow>
                    )}
                    <DetailRow label={t('rma.legacyOldIccid', 'Legacy Old ICCID')} value={legacyOldIccid} />
                    <DetailRow label={t('rma.legacyNewIccid', 'Legacy New ICCID')} value={legacyNewIccid} />
                    <DetailRow
                      label={t('rma.legacyReplacementIccid', 'Legacy Replacement ICCID')}
                      value={legacyReplacedIccid}
                    />
                  </SectionCard>
                )}

                {/* ── Special instructions ── */}
                {(customerId !== 0 || siteGroupId !== 0) && (
                  <SectionCard title={t('rma.specialInstructions', 'Special Instructions')}>
                    {customerId !== 0 && (
                      <SpecialInstructionsCollapse
                        customer={customerId}
                        types={[1, 3]}
                        checked={customerInstructionsFollowed}
                        onToggle={() => setCustomerInstructionsFollowed(prev => !prev)}
                      />
                    )}
                    {siteGroupId !== 0 && (
                      <SiteGroupInstructionsCollapse
                        siteGroup={siteGroupId}
                        types={[1, 3]}
                        checked={siteGroupInstructionsFollowed}
                        onToggle={() => setSiteGroupInstructionsFollowed(prev => !prev)}
                      />
                    )}
                  </SectionCard>
                )}

                <SectionCard title={t('rma.rmaDevice', 'RMA Device')}>
                  {/* ── Radio device lookup ── */}
                  {noRadio ? (
                    <Box sx={{ py: 1 }}>
                      <Typography variant="body2" fontWeight="bold" sx={{ mb: 0.5 }}>
                        {t('rma.rmaDevice', 'RMA Device')}
                      </Typography>
                      <Typography variant="body2">
                        {t('rma.noRadioClickPrefixDash', 'No Radio with this RMA — click')}{' '}
                        <Box
                          component="span"
                          sx={{ color: blue[700], cursor: 'pointer' }}
                          onClick={() => setNoRadio(false)}
                        >
                          {t('rma.here', 'here')}
                        </Box>{' '}
                        {t('rma.toAddOne', 'to add one.')}
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ py: 1 }}>
                      <RadioValidationBox
                        name="old_radio"
                        title={t('rma.rmaDevice', 'RMA Device')}
                        radio={oldRadio}
                        onRadioSelect={radio => setOldRadio(radio)}
                        onInputMode={() => setOldRadio(null)}
                      />
                      {oldRadio && (
                        <Box sx={{ mt: 0.5 }}>
                          <ConflictingRMAChecker
                            ignoreRMA={rma ? rma.get('id', 0) : null}
                            radio={oldRadio.get ? oldRadio.get('id', null) : (oldRadio.id ?? null)}
                            onFetch={isOk => setNoActiveRMA(isOk)}
                          />
                        </Box>
                      )}
                      {oldRadio == null && (
                        <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                          {t('rma.noRadioWithUnit', 'No radio with the unit? Click')}{' '}
                          <Box
                            component="span"
                            sx={{ color: blue[700], cursor: 'pointer' }}
                            onClick={() => setNoRadio(true)}
                          >
                            {t('rma.here', 'here')}
                          </Box>
                        </Typography>
                      )}
                    </Box>
                  )}
                </SectionCard>

                <SectionCard title={t('rma.issue', 'Issue')}>
                  <IssueCategorySelectComponent
                    onlyShowRmaCategories={true}
                    category={issueCategory}
                    subCategory={issueSubCategory}
                    onChange={handleIssueCategoryChange}
                  />
                </SectionCard>

                {/* ── Contacts ── */}
                {customerId !== 0 && siteGroupId !== 0 && (
                  <SectionCard title={t('rma.contacts', 'Contacts')}>
                    <CreateReturnFormContacts
                      customerId={customerId}
                      siteGroupId={siteGroupId}
                      customer={customer}
                      siteGroup={siteGroup}
                      selectedReportingContact={selectedReportingContact}
                      selectedShippingContact={selectedShippingContact}
                      selectedBillingContact={selectedBillingContact}
                      onReportingChange={setSelectedReportingContact}
                      onShippingChange={setSelectedShippingContact}
                      onBillingChange={setSelectedBillingContact}
                      onReportingValidation={setReportingValidationValid}
                      onShippingValidation={setShippingValidationValid}
                      onBillingValidation={setBillingValidationValid}
                    />
                  </SectionCard>
                )}

                <SectionCard title={t('rma.shippingOptions', 'Shipping & Options')}>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <ShippingModeSelect
                        value={values.inboundShippingMode}
                        onChange={v => setFieldValue('inboundShippingMode', v)}
                        label={t('rma.inboundShippingMode', 'Inbound Shipping Mode')}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <ShippingModeSelect
                        value={values.outboundShippingMode}
                        onChange={v => setFieldValue('outboundShippingMode', v)}
                        label={t('rma.outboundShippingMode', 'Outbound Shipping Mode')}
                      />
                    </Grid>
                  </Grid>
                  <Box sx={{ mt: 1 }}>
                    <FormControlLabel
                      label={t('rma.isQuickShipRma', 'Is Quick Ship RMA')}
                      control={
                        <Checkbox
                          checked={values.isQuickShip}
                          onChange={e => setFieldValue('isQuickShip', e.target.checked)}
                        />
                      }
                    />
                    <FormControlLabel
                      label={t('rma.unauthorizedSwap', 'Unauthorized Swap')}
                      control={
                        <Checkbox
                          checked={values.unauthorizedSwap}
                          onChange={e => setFieldValue('unauthorizedSwap', e.target.checked)}
                        />
                      }
                    />
                  </Box>
                </SectionCard>

                <SectionCard title={t('rma.notes', 'Notes')}>
                  <SettingsTextField
                    name="comments"
                    multiline
                    minRows={4}
                    margin="normal"
                    label={t('rma.commentsProblemDescription', 'Comments (Problem Description)')}
                  />
                  <SettingsTextField
                    name="hiddenComments"
                    multiline
                    minRows={4}
                    margin="normal"
                    label={t('rma.internalCommentsHidden', 'Internal Comments (hidden from customer)')}
                  />
                </SectionCard>

                {/* ── Image upload (edit mode, non-completed RMAs) ── */}
                {isEditMode && rma.get('status', 0) !== 100 && (
                  <SectionCard title={t('rma.picture', 'RMA Picture')}>
                    <ImagePicker currentUrl={picture} file={pictureFile} onSelect={handlePictureSelect} />
                  </SectionCard>
                )}

                {/* ── Actions ── */}
                <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ pt: 1, pb: 2 }}>
                  <Button variant="outlined" color="inherit" onClick={() => navigate(-1)}>
                    {t('general.cancel', 'Cancel')}
                  </Button>
                  <Button variant="contained" color="primary" disabled={!canSubmit} onClick={submitForm}>
                    {t('general.submit', 'Submit')}
                  </Button>
                </Stack>
              </Form>
            );
          }}
        </Formik>
      </Box>
    </Box>
  );
}
